"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
const seriesVisuals = [{
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/awareness_ocean_conservation.png",
  descriptor: "Protecting marine ecosystems / Global"
}, {
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/awareness_mental_health.png",
  descriptor: "Breaking the stigma / Community"
}, {
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/awareness_urban_greening.png",
  descriptor: "Restoring city nature / Local"
}];
function SeriesGridConsumer() {
  const {
    terms,
    loading,
    error,
    refetch
  } = useEcommerceTerms();
  if (loading) {
    return <div className="grid grid-cols-1 gap-px md:grid-cols-3 bg-border">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="aspect-[5/6] bg-card animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-16 border border-border">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">
          Failed to load causes
        </p>
        <Button variant="outline" onClick={() => refetch()} className="font-mono text-xs uppercase tracking-widest">
          Retry
        </Button>
      </div>;
  }
  if (terms.length === 0) {
    return <div className="text-center py-16 border border-border">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          No causes available
        </p>
      </div>;
  }
  return <div className="relative">
      {}
      <div className="flex md:grid md:grid-cols-3 gap-px bg-border overflow-x-auto md:overflow-visible snap-x snap-mandatory scrollbar-hide -mx-6 md:mx-0 px-6 md:px-0">
        {terms.map((term, i) => {
        const visual = seriesVisuals[i % seriesVisuals.length];
        return <Link key={term.id} data-index={i} to={term.link || "#"} className="group relative block flex-shrink-0 w-[85vw] sm:w-[60vw] md:w-auto snap-center bg-card shadow-lg rounded-lg overflow-hidden">
              <div className="relative aspect-[5/6] overflow-hidden">
                {}
                <img src={term.image_url || visual.image} data-wvc-srcset={term.image_url ? undefined : `${visual.image}-wvc-srcset`} data-wvc-sizes={term.image_url ? undefined : `${visual.image}-wvc-sizes`} alt={term.name} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover grayscale transition-transform duration-[600ms] ease-out group-hover:scale-105" />

                {}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-500 group-hover:opacity-90" />

                {}
                <div className="absolute inset-0 bg-green-900/20 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {}
                <div className="absolute inset-0 flex flex-col justify-end p-10">
                  <div className="transition-transform duration-[600ms] ease-out group-hover:-translate-y-1">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-green-200 mb-4">
                      {String(term.count ?? 0).padStart(2, "0")} INITIATIVES
                    </p>
                    <h3 className="font-default font-bold text-white uppercase mb-3" style={{
                  fontSize: "3rem",
                  letterSpacing: "-0.04em",
                  lineHeight: "0.95"
                }}>
                      {term.name}
                    </h3>
                    <p className="font-sans text-sm text-green-100 leading-snug">
                      {visual.descriptor}
                    </p>
                  </div>
                </div>

                {}
                <div className="absolute bottom-10 right-10 size-12 border border-white/20 bg-white/10 backdrop-blur-md flex items-center justify-center transition-all duration-500 group-hover:bg-green-600 group-hover:border-green-600 rounded-lg">
                  <ArrowUpRight className="size-5 text-white transition-all duration-500 group-hover:text-white group-hover:rotate-45" strokeWidth={1.5} />
                </div>

                {}
                <div className="absolute top-6 left-6 right-6 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">
                    Cause / {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">
                    FOREVER
                  </span>
                </div>
              </div>
            </Link>;
      })}
      </div>
    </div>;
}
const wp_term_query_params = {
  taxonomy: "product_cat",
  per_page: 3,
  orderby: "name",
  order: "asc"
};
const wp_term_query = new WP_Term_Query(wp_term_query_params);
export default function Awareness() {
  return <section data-section-id="4523" id="awareness-calendar" className="bg-stone-50 text-green-950 py-32 px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div className="text-center mb-20 md:mb-28">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-green-700 mb-8">
            MISSION / 2024
          </p>
          <h2 className="font-default font-bold text-green-950 mb-8 text-5xl md:text-7xl lg:text-8xl" style={{
          letterSpacing: "-0.04em",
          lineHeight: "0.95"
        }}>
            Awareness{" "}
            <span className="font-serif italic font-normal">Calendar.</span>
          </h2>
          <p className="font-sans text-lg md:text-xl text-green-800 max-w-xl mx-auto">
            Keeping critical issues forever fresh in the public consciousness.
          </p>
        </div>

        {}
        <EcommerceTermsProvider className="explore-by-series-wrapper" wp_term_query={wp_term_query}>
          <SeriesGridConsumer />
        </EcommerceTermsProvider>

        {}
        <div className="mt-20 md:mt-24 pt-10 border-t border-green-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="hidden md:block flex-1 h-px bg-green-200" />
            <Link to="/CurrentCausesPage" className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-green-950 hover:text-green-600 transition-colors duration-300">
              <span>Explore All Causes</span>
              <ArrowUpRight className="size-4 transition-transform duration-500 group-hover:rotate-45 group-hover:text-green-600" strokeWidth={1.5} />
            </Link>
            <div className="hidden md:block flex-1 h-px bg-green-200" />
          </div>
        </div>
      </div>
    </section>;
}